<template>
  <div
    :id="chartId"
    class="chart-stacked"
  >
    <div
      ref="chartArea"
      :class="[{ 'has-y-axis': hasYAxis }, `svg-wrapper svg-wrapper-${chartId}`]"
      :style="extraPadding"
    >
      <svg></svg>
    </div>
    <div
      :ref="`chart-popover-${chartId}`"
      tabindex="0"
      class="outline-none"
      @focusout="popoverFocusOut"
    >
      <AppChartPopover
        v-if="hasPopoverData"
        :id="`chart-popover-${chartId}`"
        :position="popoverPosition"
        :trigger="popoverTrigger"
        :column-data="currentColumnData"
        :custom-class="customClass"
        :strategy="popoverStrategy"
        :arrow-size="arrowSize"
        @close="triggerHidePopover"
      >
        <template #popover-header>
          <slot
            name="popover-header"
            :column-data="currentColumnData"
          />
        </template>

        <template #popover-content>
          <slot
            name="popover-content"
            :column-data="currentColumnData"
          />
        </template>
      </AppChartPopover>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

import { ChartStacked } from '~/mixins';

export default {
  name: 'AppChartStacked',
  mixins: [ChartStacked],
  props: {
    customClass: {
      type: String,
      default: '',
    },
    tickCount: {
      type: Number,
      default: null,
    },
    popoverStrategy: {
      type: String,
      default: 'window',
      validator: (value) => ['window', 'container'].includes(value),
    },
    showBarLabels: {
      type: Boolean,
      default: false,
    },
    minimumLabelHeight: {
      type: Number,
      default: 20,
    },
    arrowSize: {
      type: Number,
      default: 10,
    },
  },
  data: () => ({
    isVertical: true,
  }),
  computed: {
    chartHeight() {
      if (this.dynamicHeight) {
        return this.$refs.chartArea.clientHeight;
      }

      if (this.fixedHeight) {
        return this.fixedHeight;
      }

      return this.height;
    },
  },
  methods: {
    buildChart() {
      // Create the chart element & set initial values
      this.chart = d3.select(this.$el);
      this.svg = d3.select(this.$el).select('svg');
      this.inner = d3.select(this.$el).select('svg').append('g');

      this.cleanupTooltips();
      this.setChartColors();
      this.setChartSizing();

      this.inner.attr('transform', `translate(${this.margin.left},${this.margin.top})`);

      // Domain is the boundaries within your data e.g 1 - 100
      // Range is the chart's width in pixels
      // Bandwidth is the width of each bar
      // Max is the highest value in an array
      this.x0 = d3
        .scaleBand()
        .domain(this.formattedChartData.map((column, index) => `${column.label}__${index}`))
        .range([0, this.width])
        .paddingInner(this.columnSpacing);

      this.x1 = d3.scaleBand().range([0, this.x0.bandwidth()]);

      const maxValue = this.yDomainHeight;
      const yDomain = this.hasYAxis
        ? [0, Math.ceil(maxValue / 10) * 10] // let chart height be not just maxValue, but the nearest 10 above
        : [0, maxValue];

      this.y = d3.scaleLinear().range([this.height, 0]).domain(yDomain);

      this.generateXAxis();

      if (this.hasYAxis) {
        this.generateYAxis();
      }

      this.createBars();

      this.loading = false;
    },
    updateChart() {
      this.stopResizeObserver();
      this.createBars();
      this.createBarLabel();
      this.setupResizeObserver();
    },
    createBarLabel() {
      if (!this.showBarLabels) {
        return;
      }

      // Remove existing labels before creating new ones
      this.svg.selectAll('.bar .bar-label').remove();

      this.svg.selectAll('.bar .section').each((d, i, nodes) => {
        const bar = d3.select(nodes[i]);
        const barGroup = d3.select(nodes[i].parentElement);
        const bbox = nodes[i].getBBox();
        const barX = bbox.x;
        const barY = bbox.y;
        const barWidth = bbox.width;
        const barHeight = bbox.height;
        const sectionData = bar.data()[0];

        if (barHeight < this.minimumLabelHeight) {
          return;
        }

        const label = barGroup
          // Insert text before the rect element so rect is on top and fully covers hover for popover
          .insert('text', 'rect')
          .text(sectionData.label)
          .attr('text-anchor', 'middle')
          .attr('alignment-baseline', 'middle')
          .attr('x', barX + barWidth / 2)
          .attr('y', barY + barHeight / 2)
          .attr('class', `bar-label bar-label-${sectionData.name}`);

        if (sectionData.labelColor) {
          label.attr('fill', sectionData.labelColor);
        }
      });
    },
    createBarSection(data, radius, isEmpty = false) {
      let _vEnd = this.y(data.vEnd);

      // If the bar is empty, we draw 1px line
      if (isEmpty) {
        _vEnd = this.y(data.vEnd) - 1;
      }

      const barWidth = this.fixedWidthBars ? this.fixedWidthBars : this.x1.bandwidth();

      return `M${0},${_vEnd + radius}
        a${radius},${radius} 0 0 1 ${radius},${-radius}
        h${barWidth - 2 * radius}
        a${radius},${radius} 0 0 1 ${radius},${radius}
        v${this.y(data.vBegin) - _vEnd - radius}
        h${-barWidth}Z
        `;
    },
    generateXAxis() {
      this.inner
        .attr('class', 'xAxis')
        .attr('transform', `translate(0, ${this.height})`)
        .call(d3.axisBottom().scale(this.x0))
        .selectAll('.tick text')
        .call(this.labelCustomization, this.x1.bandwidth());
    },
    generateYAxis() {
      /**
       * The goal is to have y axis higher than the max column bar value
       * with the ticks rounded to the nearest 10
       *
       * maxValue { number } - maximum value of the column bars
       *
       * @example
       *
       * maxValue - 22
       * nearest 10 - 30
       *
       * maxValue - 18
       * nearest 10 - 20
       *
       * -----------------
       *
       * @example
       *
       * maxValue - 22
       *
       * domain([Math.ceil(maxValue / 10) * 10, 0] - maximum tick value - 30
       *
       * Math.ceil(maxValue / 10)) - maximum ticks - 3 (10, 20, 30)
       *
       *
       * 30 | ---------
       *    | --------- 22 { maxValue }
       * 20 | ---------
       *    | ---------
       * 10 | ---------
       *    | ---------
       *  0 |
       *
       */

      const maxValue = this.yDomainHeight;
      const maxWidth = this.fixedWidthBars
        ? Math.max(this.formattedChartData.length * this.fixedWidthBars * 2, window.screen.width)
        : this.width;

      this.svg
        .append('g')
        .attr('transform', 'translate(1,0)') // This controls the vertical position of the Axis
        .attr('class', 'yAxis')
        .call(
          d3
            .axisLeft(
              d3
                .scaleLinear()
                .domain([Math.ceil(maxValue / 10) * 10, 0])
                .range([0, this.height]),
            )
            .ticks(this.tickCount || Math.min(Math.ceil(maxValue / 10), 10))
            .tickSize(-maxWidth) // let the axes be 100% width of the chart
            .tickFormat((d) => `${d}${this.yTickFormat}`),
        );

      // Add an 8px horizontal dark line tick to each value on the yAxis according to the design
      this.svg.selectAll('g.xAxis ~ g > g.tick').each(function () {
        const tick = d3.select(this);

        tick.append('line').attr('x1', 8).attr('stroke', '#777777');
      });

      // Add a 6px vertical dark line tick to each value on the xAxis
      this.svg.selectAll('g.xAxis > g.tick').each(function () {
        const tick = d3.select(this);

        tick.append('line').attr('y1', 6).attr('stroke', '#777777');
      });
    },
    updateAxis() {
      this.svg
        .selectAll('.xAxis')
        .call(d3.axisBottom().scale(this.x0))
        .selectAll('.tick text')
        .call(this.labelCustomization, this.x1.bandwidth());

      const that = this;

      this.svg.selectAll('.yAxis g.tick').each(function () {
        const tick = d3.select(this);
        const line = tick.select('line');

        line.attr('x2', that.width);
      });
    },
    resizeChart() {
      const chartWidth = parseInt(this.chart.style('width'));
      const contentWidth = this.svg.selectAll('.bar').size() * this.fixedWidthBars * 2;

      if (this.fixedWidthBars && contentWidth > chartWidth) {
        this.width = contentWidth - (this.hasYAxis ? this.yAxisWidth : 0);
      } else {
        this.width = chartWidth - this.margin.left - this.margin.right - (this.hasYAxis ? this.yAxisWidth : 0);
      }

      this.height = parseInt(this.chart.style('height')) - this.margin.top - this.margin.bottom;
      this.svg.attr('class', 'chart-svg').attr('width', this.width + this.margin.left + this.margin.right);

      this.x0 = d3
        .scaleBand()
        .domain(this.formattedChartData.map((column, index) => `${column.label}__${index}`))
        .range([0, this.width])
        .paddingInner(this.columnSpacing)
        .paddingOuter(this.paddingOuter);

      this.x1 = d3.scaleBand().range([0, this.x0.bandwidth()]);

      if (this.fixedWidthBars) {
        this.bars = this.svg.selectAll('.bar').attr('transform', (column, index) => {
          const xVal = this.x0(`${column.label}__${index}`) + this.x0.bandwidth() / 2 - this.fixedWidthBars / 2;

          return `translate(${xVal},0)`;
        });
      } else {
        this.bars = this.svg.selectAll('.bar').attr('transform', (column, index) => {
          const xVal = this.x0(`${column.label}__${index}`);

          return `translate(${xVal},0)`;
        });
      }

      if(this.forcedAmountOfBars) {
        this.$refs.chartArea.style.width = `${chartWidth}px`
      }

      this.drawBars();
      this.createBarLabel();
      this.updateAxis();
    },
  },
};
</script>
